import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ChevronRight, ArrowRight, CheckCircle, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import QuickInquiryForm from './QuickInquiryForm';

const WHATSAPP_URL = 'https://wa.me/917757999805?text=Hi%20Aquarist%20Pools%2C%20I%20would%20like%20to%20enquire%20about%20your%20pool%20services.';

const ServicePage = ({ title, subtitle, heroImage, intro, features, process, faqs, relatedServices, breadcrumb, schemaData }) => {

  useEffect(() => {
    const injected = [];

    const inject = (id, data) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      injected.push(id);
    };

    if (schemaData) inject('service-page-schema', schemaData);

    // Auto-generate FAQPage schema from the faqs prop for AI / Google rich-results
    if (faqs && faqs.length > 0) {
      inject('service-page-faq-schema', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    return () => {
      injected.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [schemaData, faqs]);

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-1.5 text-sm text-gray-500" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#00AEEF]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/services" className="hover:text-[#00AEEF]">Services</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#003366] font-medium">{breadcrumb || title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#003366] via-[#002244] to-[#001a33] overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 text-white">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">{title}</h1>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">{subtitle}</p>
              <a href="tel:+917757999805" className="inline-flex items-center gap-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-5 py-2.5 mb-6 transition-all" data-testid="service-call-cta">
                <Phone className="w-4 h-4 text-[#00AEEF]" />
                <div>
                  <span className="text-xs text-gray-400 block leading-none">Call for Consultation</span>
                  <span className="text-white font-bold text-sm">+91 77579 99805</span>
                </div>
              </a>
              <div className="flex flex-wrap gap-3">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm">
                  <MessageCircle className="w-4 h-4 fill-white" /> WhatsApp Us
                </a>
                <Button asChild className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-6 py-3 font-semibold">
                  <Link to="/booking">Get Free Quote <ArrowRight className="ml-1.5 w-4 h-4" /></Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-2 hidden lg:block">
              <QuickInquiryForm variant="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none prose-headings:text-[#003366] prose-p:text-gray-700 prose-strong:text-[#003366] prose-li:text-gray-700" dangerouslySetInnerHTML={{ __html: intro }} />
        </div>
      </section>

      {/* Features */}
      {features && features.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#003366] text-center mb-10">What We Offer</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center mb-4">
                      <CheckCircle className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <h3 className="font-bold text-[#003366] mb-2">{f.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      {process && process.length > 0 && (
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#003366] text-center mb-10">Our Process</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {process.map((step, i) => (
                <div key={i} className="text-center">
                  <div className="text-5xl font-bold text-[#00AEEF]/20 mb-2">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="font-bold text-[#003366] mb-1">{step.title}</h3>
                  <p className="text-sm text-gray-500">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#003366] text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <summary className="flex items-center gap-3 p-5 cursor-pointer font-semibold text-[#003366] hover:text-[#00AEEF] transition-colors list-none">
                    <HelpCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0" />
                    {faq.q}
                  </summary>
                  <div className="px-5 pb-5 pl-12 text-sm text-gray-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Services */}
      {relatedServices && relatedServices.length > 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#003366] text-center mb-10">Related Services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedServices.map((s, i) => (
                <Link key={i} to={s.link} className="group">
                  <Card className="border-0 shadow-md hover:shadow-xl transition-all h-full">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-[#003366] group-hover:text-[#00AEEF] transition-colors mb-2">{s.title}</h3>
                      <p className="text-sm text-gray-500 mb-3">{s.desc}</p>
                      <span className="text-xs font-semibold text-[#00AEEF] flex items-center gap-1">Learn More <ArrowRight className="w-3 h-3" /></span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-r from-[#003366] to-[#001a33] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Get Your Free Consultation Today</h2>
          <p className="text-gray-300 mb-8">Speak to Bangalore's top pool construction experts. Free site evaluation, transparent pricing, and expert design advice.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg">
              <MessageCircle className="w-5 h-5 fill-white" /> Chat on WhatsApp
            </a>
            <a href="tel:+917757999805" className="flex items-center gap-2 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg">
              <Phone className="w-5 h-5" /> Call +91 77579 99805
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
